import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import { selectAccessLevel } from '@/slices/subscriptions/subscriptionsSlice';
import { SubscriptionAccess } from '@/types/Subscription';
import type { ChatDailyLimits } from '@/types/chatStream';

interface ChatLimitMeterProps {
  limits: ChatDailyLimits | null;
}

/**
 * The daily chat allowance, as the server reported it on the last `turn.start`
 * or `turn.done`. One user message is one chat however many tools the turn
 * runs, so this only moves once per send.
 *
 * Nothing renders until the server has told us a cap: an uncapped user should
 * never see a meter, and inventing one client-side is how the legacy panel's
 * fallback constant drifted from the real limit.
 *
 * Only the free plan sees it (ORA-511). The server reports the global cap to
 * everyone, and a paid student has no use for "x of 600"; for a free student
 * the meter is the one cue for why a send is refused. Enforcement does not
 * depend on it — `useChatSend` reads the remaining count itself.
 */
const ChatLimitMeter = ({ limits }: ChatLimitMeterProps) => {
  const accessLevel = useSelector(selectAccessLevel);

  if (accessLevel !== SubscriptionAccess.FREE) return null;
  if (limits?.chatsLimit == null || limits.chatsRemaining == null) return null;

  const { chatsLimit, chatsRemaining } = limits;
  const isSpent = chatsRemaining <= 0;

  return (
    // The meter keeps its place at the end of the composer's control row from
    // `mobile` up. On a phone that row is the one line the text box sits on
    // (ORA-628), where a sentence of text would leave nothing to type in, so
    // the meter takes a line of its own above it instead of being dropped —
    // for a free student it is the one cue for why a send is refused.
    <p
      className={cn(
        'm-0 text-xs max-mobile:-order-1 max-mobile:w-full max-mobile:px-1',
        isSpent ? 'text-status-danger' : 'text-fg-secondary'
      )}
      data-testid="chat-limit-meter"
    >
      {isSpent
        ? `You have used all ${chatsLimit} of today's chats.`
        : `${chatsRemaining} of ${chatsLimit} daily chats left`}
    </p>
  );
};

export default ChatLimitMeter;
